import { Checkbox } from '../checkbox'
import { closeModal, UPDATE_PASSWORD_MODAL } from 'spa/action_creators/modals'
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Label } from 'spa/components/label'
import { SpinnerButton } from 'spa/components/button'
import { TextInput } from 'spa/components/text_input'
import { updatePassword } from 'spa/action_creators/current_user'
import { useDispatch, useSelector } from 'react-redux'
import { userEmailSelector } from 'spa/selectors/users'
import ApplicationModal from 'spa/components/application_modal'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'

import { isMinimumCharacters } from '../../shared/utils'
import styles from './styles'

const localizedMessages = defineMessages({
  newPassword: {
    id: 'djcity.account.update_password.new_password',
    defaultMessage: 'New Password',
  },
  confirmNewPassword: {
    id: 'djcity.account.update_password.confirm_new_password',
    defaultMessage: 'Confirm New Password',
  },
  oldPassword: {
    id: 'djcity.account.update_password.old_password',
    defaultMessage: 'Current Password',
  },
})

function UpdatePasswordModal({ intl }) {
  const dispatch = useDispatch()
  const isOpen = useSelector(
    state => state.modals[UPDATE_PASSWORD_MODAL].enabled
  )
  const isResettingPassword = useSelector(state => state.isResettingPassword)
  const email = useSelector(userEmailSelector)
  const [newPassword, setNewPassword] = useState('')
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('')
  const [oldPassword, setOldPassword] = useState('')
  const [passwordHidden, setPasswordHidden] = useState(true)
  const [logoutFromAllDevices, setLogoutFromAllDevices] = useState(true)

  const isDisabled = !(
    email &&
    newPassword &&
    newPasswordConfirmation &&
    oldPassword &&
    isMinimumCharacters(newPassword, 8) &&
    isMinimumCharacters(newPasswordConfirmation, 8)
  )

  useEffect(() => {
    setNewPassword('')
    setNewPasswordConfirmation('')
    setOldPassword('')
    setLogoutFromAllDevices(true)
  }, [
    isOpen,
    setNewPassword,
    setNewPasswordConfirmation,
    setOldPassword,
    setLogoutFromAllDevices,
  ])

  const onSubmit = async e => {
    e.preventDefault()
    if (!isDisabled) {
      await dispatch(
        updatePassword(
          {
            email,
            newPassword,
            newPasswordConfirmation,
            oldPassword,
            logoutFromAllDevices,
          },
          true
        )
      )
      dispatch(closeModal())
    }
  }

  return (
    <ApplicationModal
      modalKey={UPDATE_PASSWORD_MODAL}
      title={
        <FormattedMessage
          defaultMessage="Update Password"
          id="djcity.account.update_password.title"
        />
      }
    >
      <form className={styles.updatePasswordForm} onSubmit={onSubmit}>
        <Label
          className={styles.label}
          text={intl.formatMessage(localizedMessages.oldPassword)}
        >
          <TextInput
            className={classNames(styles.input, styles.passwordInput)}
            onChange={e => setOldPassword(e.target.value)}
            type={passwordHidden ? 'password' : 'text'}
            value={oldPassword}
          />
          <FontAwesomeIcon
            className={styles.password}
            icon={passwordHidden ? 'eye-slash' : 'eye'}
            onClick={() => setPasswordHidden(!passwordHidden)}
          />
        </Label>
        <Label
          className={styles.label}
          text={intl.formatMessage(localizedMessages.newPassword)}
        >
          <TextInput
            className={classNames(styles.input, styles.passwordInput)}
            onChange={e => setNewPassword(e.target.value)}
            type={passwordHidden ? 'password' : 'text'}
            value={newPassword}
          />
          <FontAwesomeIcon
            className={styles.password}
            icon={passwordHidden ? 'eye-slash' : 'eye'}
            onClick={() => setPasswordHidden(!passwordHidden)}
          />
        </Label>
        <Label
          className={styles.label}
          text={intl.formatMessage(localizedMessages.confirmNewPassword)}
        >
          <TextInput
            className={classNames(styles.input, styles.passwordInput)}
            onChange={e => setNewPasswordConfirmation(e.target.value)}
            type={passwordHidden ? 'password' : 'text'}
            value={newPasswordConfirmation}
          />
          <FontAwesomeIcon
            className={styles.password}
            icon={passwordHidden ? 'eye-slash' : 'eye'}
            onClick={() => setPasswordHidden(!passwordHidden)}
          />
        </Label>
        <div className={styles.checkboxContainer}>
          <Checkbox
            onChange={() => setLogoutFromAllDevices(!logoutFromAllDevices)}
            selected={logoutFromAllDevices}
          ></Checkbox>
          <FormattedMessage
            defaultMessage="Log out from all devices (including this one)"
            id="djcity.account.logout_from_all_devices"
          />
        </div>
        <SpinnerButton
          disabled={isDisabled || isResettingPassword}
          loading={isResettingPassword}
          type="submit"
        >
          <FormattedMessage
            defaultMessage="Update Password"
            id="djcity.account.update_password.button"
          />
        </SpinnerButton>
      </form>
    </ApplicationModal>
  )
}

UpdatePasswordModal.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
}

export default injectIntl(UpdatePasswordModal)
